import { Card } from "@/components/ui/card";
import { ChartsLoader } from "@/components/charts/ChartsLoader";

export interface MarketingInsightsPayload {
  best_times?: Record<string, string[]>;
  engagement_heatmap?: Array<{ day: string; hour: number; value: number }>;
  performance_last_30d?: Array<{ date: string; engagement: number; reach: number }> | Record<string, unknown>;
  posting_frequency_recommendations?: Record<string, string>;
}

interface InsightsPanelProps {
  data?: MarketingInsightsPayload;
  loading?: boolean;
}

export function InsightsPanel({ data, loading }: InsightsPanelProps) {
  const bestTimes = data?.best_times ?? {};
  const heatmap = data?.engagement_heatmap ?? [];
  const performanceRaw = Array.isArray(data?.performance_last_30d)
    ? (data!.performance_last_30d as Array<{ date: string; engagement: number; reach: number }>)
    : [];
  const posting = data?.posting_frequency_recommendations ?? {};

  return (
    <Card className="space-y-4 border-border/70 bg-background/40 p-4">
      <div>
        <p className="text-sm font-semibold text-foreground">Marketing Insights</p>
        <p className="text-xs text-muted-foreground">AI-curated engagement guidance across platforms.</p>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading insights…</p>
      ) : (
        <div className="space-y-4">
          <section>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Best Times</p>
            <div className="mt-2 grid gap-3 md:grid-cols-3">
              {Object.entries(bestTimes).map(([platform, times]) => (
                <div key={platform} className="rounded-xl border border-border/70 bg-background/40 p-3">
                  <p className="text-sm font-semibold text-foreground">{platform}</p>
                  <p className="text-xs text-muted-foreground">{times.join(", ") || "No data"}</p>
                </div>
              ))}
              {Object.keys(bestTimes).length === 0 ? (
                <p className="text-sm text-muted-foreground">No timing data yet.</p>
              ) : null}
            </div>
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-border/70 bg-background/40 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Engagement Heatmap</p>
              {heatmap.length === 0 ? (
                <p className="text-sm text-muted-foreground">No heatmap data.</p>
              ) : (
                <div className="mt-2 h-60">
                  <ChartsLoader
                    fallback={<p className="text-xs text-muted-foreground">Loading chart…</p>}
                    errorFallback={<p className="text-xs text-muted-foreground">Chart unavailable.</p>}
                  >
                    {({ ResponsiveContainer, ScatterChart, CartesianGrid, XAxis, YAxis, ZAxis, Tooltip, Scatter }) => (
                      <ResponsiveContainer width="100%" height="100%">
                        <ScatterChart margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                          <XAxis dataKey="hour" tick={{ fill: "#94a3b8", fontSize: 12 }} stroke="#1e293b" />
                          <YAxis dataKey="day" tick={{ fill: "#94a3b8", fontSize: 12 }} stroke="#1e293b" />
                          <ZAxis dataKey="value" range={[0, 400]} />
                          <Tooltip cursor={{ strokeDasharray: "3 3" }} contentStyle={{ backgroundColor: "#020617", borderColor: "#1e293b" }} />
                          <Scatter data={heatmap} fill="#fbbf24" />
                        </ScatterChart>
                      </ResponsiveContainer>
                    )}
                  </ChartsLoader>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border/70 bg-background/40 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Performance (30d)</p>
              {performanceRaw.length === 0 ? (
                <p className="text-sm text-muted-foreground">No trend data.</p>
              ) : (
                <div className="mt-2 h-60">
                  <ChartsLoader
                    fallback={<p className="text-xs text-muted-foreground">Loading chart…</p>}
                    errorFallback={<p className="text-xs text-muted-foreground">Chart unavailable.</p>}
                  >
                    {({ ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Line }) => (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={performanceRaw} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                          <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 12 }} stroke="#1e293b" />
                          <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} stroke="#1e293b" />
                          <Tooltip contentStyle={{ backgroundColor: "#020617", borderColor: "#1e293b" }} />
                          <Line type="monotone" dataKey="engagement" stroke="#fbbf24" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="reach" stroke="#38bdf8" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </ChartsLoader>
                </div>
              )}
            </div>
          </section>

          <section>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Posting Cadence</p>
            <div className="mt-2 grid gap-3 md:grid-cols-3">
              {Object.entries(posting).map(([platform, recommendation]) => (
                <div key={platform} className="rounded-xl border border-border/70 bg-background/40 p-3">
                  <p className="text-sm font-semibold text-foreground">{platform}</p>
                  <p className="text-xs text-muted-foreground">{recommendation}</p>
                </div>
              ))}
              {Object.keys(posting).length === 0 ? (
                <p className="text-sm text-muted-foreground">No cadence recommendations.</p>
              ) : null}
            </div>
          </section>
        </div>
      )}
    </Card>
  );
}
